"use client";

import { changeSign, signedNumber } from "@/lib/format/change";
import { formatPrice } from "@/lib/api";
import { formatMonthDay } from "@/lib/blog-format";
import type React from "react";

/**
 * Client side of the district trend page. Kept separate from the
 * server page.tsx so the outer page can do its data fetch +
 * metadata generation without shipping React hooks to the build,
 * and the interactive pieces (TrendChart with its ApexCharts bundle,
 * SupplySignals with its own client fetch) stay isolated here.
 *
 * The trend chart and supply signals components each do their own
 * fetch to /api/trends and /api/trends/supply respectively - the
 * server already fetched the summary with this data, but the
 * existing components take `level/locationKey/mode` props and
 * fetch on mount, so we honour that contract rather than refactor
 * two established components just for this page.
 */
import { useMemo } from "react";
import Link from "@/lib/localized-link";
import { mediaSrc } from "@/lib/api";
import type { Locale } from "@/lib/i18n-config";
import { t as translate, type TranslationKey } from "@/lib/i18n";
import { useLocale } from "@/lib/locale-context";
import type { District } from "@/lib/districts";
import SharedHeader from "@/components/SharedHeader";
import TrendChart from "@/components/TrendChart";
import FaqList from "@/components/FaqList";
import WoWBadge from "@/components/trends/WoWBadge";
import SectionHeader from "@/components/trends/SectionHeader";
import RoomsGrid from "@/components/trends/RoomsGrid";
import ComparisonRow from "@/components/trends/ComparisonRow";
import InterestCard from "@/components/demand/InterestCard";
import Photo from "@/components/Photo";
import {
  ArrowRight,
  BarChart2,
  Building2,
  Home,
  TrendingUp,
} from "lucide-react";

interface SummaryShape {
  latest: { median_ppm: number | null; median_price: number | null; listing_count: number } | null;
  wow_ppm_pct: number | null;
  supply: {
    active_listings: number;
    avg_days_on_market: number | null;
    inventory_months: number | null;
    price_reduction_rate: number;
  };
  rooms: Array<{ room_bucket: number; listing_count: number; median_ppm: number | null; median_price: number | null; mom_ppm_pct?: number | null }>;
  yield_pct: number | null;
  median_monthly_rent: number | null;
  comparison?: {
    baku_median_ppm: number | null;
    baku_week?: string | null;
    baku_as_of?: string | null;
    baku_listings?: number;
    neighbors: Array<{ slug: string | null; name: string; median_ppm: number | null }>;
  };
  top_complexes?: Array<{
    name: string;
    cluster_id?: string | null;
    listing_count: number;
    median_ppm: number;
    median_price?: number | null;
    image_url?: string | null;
  }>;
}

interface Props {
  /** Server-rendered extras placed after the FAQ (the search-engine
   *  bridge: this place's searches, comparisons, price check). Passed
   *  as children so they stay server components with crawlable links. */
  children?: React.ReactNode;
  /** Server-rendered locale, used only as the seed for the hook
   *  context's initial value. All display translations read from the
   *  LocaleContext so the header's language toggle (which swaps the
   *  URL prefix via history.replaceState, not a Next navigation)
   *  takes effect on this page too. Without that, the locale would
   *  freeze on whatever was server-rendered. */
  locale: Locale;
  district: District;
  summary: SummaryShape;
  /** Rent-mode summary. Optional because rent data is sparser - if
   *  absent we skip the rent twin block rather than fail the page. */
  rentSummary?: SummaryShape | null;
  /** 52-week price change (%), computed server-side from the raw trend
   *  series (which isn't plumbed to the client). Passed in so the
   *  visible YoY FAQ answer matches the figure in the JSON-LD schema. */
  yoyPct?: number | null;
}

function formatPpm(n: number | null | undefined): string {
  if (!n) return "-";
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

function formatMoney(n: number | null | undefined, locale: Locale): string {
  if (!n) return "-";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K AZN`;
  return `${n} AZN`;
  void locale;
}

// Room bucket -> localized chip label.
function roomLabel(bucket: number, locale: Locale): string {
  if (bucket === 0) return "Studio";
  if (bucket >= 4) return translate("district.rooms.4plus", locale);
  return `${bucket} ${translate("district.rooms.suffix", locale)}`;
}

// Locale-aware string substitution helper - Tailwind keys use
// literal {name}/{pct}/{n} slots (see src/lib/i18n.ts).
function fill(template: string, vars: Record<string, string | number>): string {
  return Object.entries(vars).reduce(
    (acc, [k, v]) => acc.split(`{${k}}`).join(String(v)),
    template,
  );
}

/**
 * Data-driven SEO intro rendered at the top of every trend slug page.
 * Each of the ~67 districts emits a factually-unique 2-3 sentence
 * paragraph because the median ppm, active-listing count, WoW delta,
 * and Baku-comparison class are all read from summary. The i18n
 * keys hold the template fragments in all three locales; the
 * ppm-threshold tiers and +-% comparison classes are deterministic so
 * the output stays stable across renders of the same data.
 *
 * Why not LLM-generated per-slug prose? LLM copy at 67x3 pages risks
 * "thin / boilerplate" demotion from Google unless heavily edited;
 * data-driven templates are what Zillow/Redfin do for the same
 * problem because each page's numbers IS the unique content.
 */
function buildDistrictIntro(
  districtName: string,
  summary: SummaryShape,
  locale: Locale,
): string | null {
  const ppm = summary.latest?.median_ppm ?? null;
  const active = summary.latest?.listing_count ?? 0;
  if (!ppm || active === 0) return null;

  const tt = (k: TranslationKey) => translate(k, locale);

  // Price tier from ppm. Thresholds are rounded boundaries across the
  // observed Baku distribution (2024-2026 snapshots): budget/lower
  // cluster near ~1000-1800 AZN, mid/upper stretch ~1800-3200 AZN,
  // premium > 3200 AZN. Hardcoding these is more honest than recomputing
  // quantiles at render time - the thresholds are stable and the
  // copy's meaning ("en bahali") is anchored to absolute price, not a
  // percentile that could flip week to week.
  let tier: TranslationKey;
  if (ppm >= 3500) tier = "district.intro.tier.premium";
  else if (ppm >= 2500) tier = "district.intro.tier.upper";
  else if (ppm >= 1800) tier = "district.intro.tier.mid";
  else if (ppm >= 1200) tier = "district.intro.tier.lower";
  else tier = "district.intro.tier.budget";

  // WoW direction. 0.5% hysteresis mirrors the hero chip threshold
  // so the intro and the chip never disagree about "up / down / flat".
  const wow = summary.wow_ppm_pct;
  let wowSentence: string;
  if (wow == null || Math.abs(wow) < 0.5) {
    wowSentence = tt("district.intro.wow.flat");
  } else if (wow > 0) {
    wowSentence = fill(tt("district.intro.wow.up"), { pct: Math.abs(wow).toFixed(1) });
  } else {
    wowSentence = fill(tt("district.intro.wow.down"), { pct: Math.abs(wow).toFixed(1) });
  }

  // Comparison to Baku median. Suppressed entirely when
  // summary.comparison is missing (e.g. thin district) - the sentence
  // would be awkward without a concrete "above/below" to anchor it.
  const baku = summary.comparison?.baku_median_ppm ?? null;
  let compareSentence = "";
  if (baku && baku > 0) {
    const pct = ((ppm - baku) / baku) * 100;
    let compareKey: TranslationKey;
    if (pct > 10) compareKey = "district.intro.compare.higher_much";
    else if (pct > 3) compareKey = "district.intro.compare.higher";
    else if (pct < -10) compareKey = "district.intro.compare.lower_much";
    else if (pct < -3) compareKey = "district.intro.compare.lower";
    else compareKey = "district.intro.compare.near";
    compareSentence = tt(compareKey);
  }

  // Active count + ppm rendered with NBSP grouping (U+00A0 between
  // thousand groups) so numbers never wrap inside the sentence.
  const fmt = (n: number) =>
    Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  return fill(tt("district.intro.template"), {
    name: districtName,
    tier: tt(tier),
    active: fmt(active),
    ppm: fmt(ppm),
    wow: wowSentence,
    compare: compareSentence,
  }).trim();
}

/** Locale-reactive FAQ builder - mirrors the server-side buildFaq in
 *  page.tsx, rebuilt here so the language toggle (which doesn't
 *  re-run the server component) updates the visible accordion. Keep
 *  the shape identical so page.tsx's FAQPage JSON-LD keeps working
 *  for crawlers. */
function buildClientFaq(
  district: District,
  summary: SummaryShape,
  locale: Locale,
  yoyPct?: number | null,
): Array<{ q: string; a: string }> {
  const tt = (k: TranslationKey) => translate(k, locale);
  const fmt = (n: number | null | undefined) =>
    n ? Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ") : "-";

  const out: Array<{ q: string; a: string }> = [];

  const medPrice = summary.latest?.median_price ?? null;
  if (medPrice) {
    out.push({
      q: fill(tt("district.faq.avgPrice.q"), { name: district.name }),
      a: fill(tt("district.faq.avgPrice.a"), { name: district.name, price: fmt(medPrice) }),
    });
  }
  const medPpm = summary.latest?.median_ppm ?? null;
  if (medPpm) {
    out.push({
      q: fill(tt("district.faq.avgPpm.q"), { name: district.name }),
      a: fill(tt("district.faq.avgPpm.a"), { name: district.name, ppm: fmt(medPpm) }),
    });
  }
  // 52-week change: the raw trend series isn't plumbed to the client,
  // so the server passes the already-computed percentage in via `yoyPct`.
  // Mirror page.tsx's buildFaq thresholds exactly so the visible answer
  // matches the figure in the JSON-LD schema (a rich-result requirement).
  let yoyAnswer: string;
  if (yoyPct == null) {
    yoyAnswer = tt("district.faq.yoy.a.na");
  } else if (yoyPct > 0.5) {
    yoyAnswer = fill(tt("district.faq.yoy.a.up"), { pct: yoyPct.toFixed(1) });
  } else if (yoyPct < -0.5) {
    yoyAnswer = fill(tt("district.faq.yoy.a.down"), { pct: Math.abs(yoyPct).toFixed(1) });
  } else {
    yoyAnswer = tt("district.faq.yoy.a.flat");
  }
  out.push({
    q: fill(tt("district.faq.yoy.q"), { name: district.name }),
    a: yoyAnswer,
  });

  const twoRoom = summary.rooms?.find((r) => r.room_bucket === 2);
  out.push({
    q: fill(tt("district.faq.twoRoom.q"), { name: district.name }),
    a: twoRoom?.median_price
      ? fill(tt("district.faq.twoRoom.a"), { name: district.name, price: fmt(twoRoom.median_price) })
      : tt("district.faq.twoRoom.a.na"),
  });
  out.push({
    q: fill(tt("district.faq.yield.q"), { name: district.name }),
    a: summary.yield_pct != null
      ? fill(tt("district.faq.yield.a"), { name: district.name, yield: summary.yield_pct.toFixed(1) })
      : tt("district.faq.yield.a.na"),
  });
  return out;
}

export default function DistrictTrendPageClient({
  locale: initialLocale,
  district,
  summary,
  rentSummary,
  yoyPct,
  children,
}: Props) {
  // Read from context so the page reacts to the header's language
  // toggle (which rewrites the URL but doesn't re-run the server
  // component). Seeded by LocaleProvider with the URL's locale, so
  // first render matches the server HTML and hydration is clean.
  const { locale: ctxLocale } = useLocale();
  const locale = ctxLocale || initialLocale;
  const t = (k: TranslationKey) => translate(k, locale);

  // FAQ - rebuilt locally from the same summary data page.tsx uses
  // for its JSON-LD schema, so switching language updates the visible
  // accordion without a server round-trip. The JSON-LD remains in the
  // initial-HTML language because that's what Googlebot indexed at
  // that URL; the two don't need to match once the user is here.
  const faq = useMemo(
    () => buildClientFaq(district, summary, locale, yoyPct),
    [district, summary, locale, yoyPct],
  );

  const ppm = summary.latest?.median_ppm ?? 0;
  const wow = summary.wow_ppm_pct;
  // Hero uses the aggregate listing_count from the latest weekly
  // bucket rather than supply.active_listings. Rationale: the hero
  // describes the market snapshot shown by the chart directly above
  // it, which is the aggregate's world. supply.active_listings comes
  // from listing_lifecycle and can diverge (e.g. local dev DBs lag
  // on lifecycle promotion) - showing it here made the hero read
  // "0 - active listings" on the same page whose chart had 725.
  const active = summary.latest?.listing_count ?? 0;

  // Baku comparison delta for hero badge
  const bakuPpm = summary.comparison?.baku_median_ppm ?? null;
  const vsBakuPct = bakuPpm && ppm ? ((ppm - bakuPpm) / bakuPpm) * 100 : null;

  return (
    <div className="min-h-screen bg-gray-50">
      <SharedHeader />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 pt-6 pb-16">

        {/* Visible breadcrumb - mirrors the BreadcrumbList JSON-LD
            shipped from the server page so the structured-data path
            Google sees matches the path the user sees. `aria-label`
            tags it as navigation for screen readers; the current
            page (the district) is a plain <span> with `aria-current`
            rather than a link, per the WAI breadcrumb pattern. */}
        <nav aria-label={t("common.breadcrumb")} className="mb-5 text-sm text-gray-500">
          <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
            <li>
              <Link href="/" className="hover:text-gray-900 transition-colors">
                {t("breadcrumb.home")}
              </Link>
            </li>
            <li aria-hidden="true" className="text-gray-300">›</li>
            <li>
              <Link href="/trends" className="hover:text-gray-900 transition-colors">
                {t("breadcrumb.trends")}
              </Link>
            </li>
            <li aria-hidden="true" className="text-gray-300">›</li>
            <li>
              <span aria-current="page" className="text-gray-900 font-medium">
                {district.name}
              </span>
            </li>
          </ol>
        </nav>

        {/* Hero card - the most prominent element on the page */}
        <header className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-7 mb-8">
          {/* Eyebrow - a clean static label; the keyword-rich title stays
              in the H1 below (these are SEO landing pages). */}
          <div className="text-xs font-bold text-gray-500 mb-2">
            {t("breadcrumb.trends")}
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight leading-tight">
            {t("district.page.title").replace("{name}", district.name)}
          </h1>

          {/* Key stats row */}
          <div className="mt-5 flex flex-wrap items-end gap-4 sm:gap-6">
            {/* Main price */}
            <div>
              <div className="text-[11px] font-semibold text-gray-400 mb-1">
                {t("trends.index.hero.avgPpm")}
              </div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl sm:text-4xl font-extrabold text-gray-900 tabular-nums leading-none">
                  {formatPpm(ppm)}
                </span>
                <span className="text-base font-semibold text-gray-500">AZN/m²</span>
                {/* WoW badge inline with the price */}
                <WoWBadge wow={wow} size="md" />
              </div>
            </div>

            {/* Divider */}
            <div className="hidden sm:block w-px h-10 bg-gray-200" />

            {/* Active listings */}
            <div>
              <div className="text-[11px] font-semibold text-gray-400 mb-1">
                {t("district.supply.active")}
              </div>
              <div className="text-2xl font-extrabold text-gray-900 tabular-nums leading-none">
                {active.toLocaleString()}
              </div>
            </div>

            {/* vs Baku badge */}
            {vsBakuPct != null && Math.abs(vsBakuPct) >= 1 && (
              <>
                <div className="hidden sm:block w-px h-10 bg-gray-200" />
                <div>
                  <div className="text-[11px] font-semibold text-gray-400 mb-1">
                    {t("district.compare.baku")}
                  </div>
                  <span
                    className={`inline-flex items-center gap-0.5 text-sm font-bold tabular-nums ${
                      changeSign(vsBakuPct, 1) > 0 ? "text-rose-600" : changeSign(vsBakuPct, 1) < 0 ? "text-emerald-700" : "text-gray-600"
                    }`}
                  >
                    {signedNumber(vsBakuPct, 1)}%
                  </span>
                </div>
              </>
            )}
          </div>

          {/* CTA button */}
          <div className="mt-6 flex items-center gap-3">
            <Link
              href="/analyze"
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 transition"
            >
              {t("district.cta.analyze")}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </header>

        {/* SEO content bump - factual 2-3 sentence intro woven from
            summary data (tier, active count, ppm, WoW, comparison to
            Baku). Renders on every /trends/<slug> page, factually
            unique per district because the numbers and classes differ.
            Addresses the "Crawled - currently not indexed" bucket in
            GSC by adding real indexable prose above the charts. */}
        {(() => {
          const intro = buildDistrictIntro(district.name, summary, locale);
          if (!intro) return null;
          return (
            <p className="mb-8 text-sm sm:text-base text-gray-600 leading-relaxed max-w-prose">
              {intro}
            </p>
          );
        })()}

        {/* Comparison section - this district vs Baku + neighbours */}
        {summary.comparison && (summary.comparison.baku_median_ppm || summary.comparison.neighbors.length > 0) && (
          <section className="mb-10">
            <SectionHeader
              title={t("district.compare.title")}
              icon={<BarChart2 className="w-4 h-4" />}
            />
            <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
              <ComparisonRow
                label={t("district.compare.this")}
                value={ppm || null}
                baku={summary.comparison.baku_median_ppm}
                emphasize
                locale={locale}
              />
              {summary.comparison.baku_median_ppm != null && (
                <ComparisonRow
                  label={t("district.compare.baku")}
                  value={summary.comparison.baku_median_ppm}
                  baku={null}
                  locale={locale}
                />
              )}
              {/* Which Bakı number this is: the one benchmark every page
                  quotes, the active listings' median AZN/m², with its count
                  and date (owner, 2026-10-04). */}
              {summary.comparison.baku_median_ppm != null && summary.comparison.baku_as_of && (
                <p className="border-t border-gray-100 px-4 py-2 text-[11px] text-gray-500">
                  {t("district.compare.baku_def")
                    .replace("{n}", formatPrice(summary.comparison.baku_listings ?? 0))
                    .replace("{date}", formatMonthDay(new Date(summary.comparison.baku_as_of).getTime(), locale))}
                </p>
              )}
              {summary.comparison.neighbors.length > 0 && (
                <div className="px-4 py-2 text-[11px] font-bold text-gray-500 bg-gray-50 border-t border-gray-100">
                  {t("district.compare.neighbors")}
                </div>
              )}
              {summary.comparison.neighbors.map((n) => (
                <ComparisonRow
                  key={n.name}
                  label={n.name}
                  value={n.median_ppm}
                  baku={summary.comparison?.baku_median_ppm ?? null}
                  href={n.slug ? `/trends/${n.slug}` : undefined}
                  locale={locale}
                />
              ))}
            </div>
          </section>
        )}

        {/* Sale price trend chart */}
        <section className="mb-10">
          <SectionHeader
            title={t("district.section.trend.sale")}
            icon={<TrendingUp className="w-4 h-4" />}
          />
          <TrendChart
            level="district"
            locationKey={district.name}
            mode="sale"
            rooms={0}
            weeks={52}
            baseline
          />
        </section>

        {/* Sale - rooms breakdown */}
        {summary.rooms.length > 0 && (
          <section className="mb-10">
            <SectionHeader
              title={t("district.section.rooms.sale")}
              icon={<Home className="w-4 h-4" />}
            />
            <RoomsGrid
              rows={summary.rooms}
              roomLabel={(b) => roomLabel(b, locale)}
              formatPpm={formatPpm}
              headers={{
                rooms: t("trends.rooms.th.rooms"),
                ppm: t("trends.rooms.th.ppm"),
                mom: t("trends.rooms.th.mom"),
                count: t("trends.rooms.th.count"),
              }}
            />
          </section>
        )}

        {/* Rent twin block - same widgets, mode="rent". Rendered as
            a stacked pair below the sale block rather than a toggle
            so search engines index both variants. Skipped entirely
            when we have neither a rent trend nor rent room data -
            many smaller districts have rent coverage too sparse to
            bother rendering an empty chart. */}
        {(rentSummary?.latest?.median_ppm || (rentSummary?.rooms?.length ?? 0) > 0) && (
          <>
            <section className="mb-10">
              <SectionHeader
                title={t("district.section.trend.rent")}
                icon={<TrendingUp className="w-4 h-4" />}
              />
              <TrendChart
                level="district"
                locationKey={district.name}
                mode="rent"
                rooms={0}
                weeks={52}
                baseline
              />
            </section>

            {rentSummary?.rooms && rentSummary.rooms.length > 0 && (
              <section className="mb-10">
                <SectionHeader
                  title={t("district.section.rooms.rent")}
                  icon={<Home className="w-4 h-4" />}
                />
                <RoomsGrid
                  rows={rentSummary.rooms}
                  roomLabel={(b) => roomLabel(b, locale)}
                  formatPpm={formatPpm}
                  headers={{
                    rooms: t("trends.rooms.th.rooms"),
                    ppm: t("trends.rooms.th.ppm"),
                    mom: t("trends.rooms.th.mom"),
                    count: t("trends.rooms.th.count"),
                  }}
                />
              </section>
            )}
          </>
        )}

        {/* Rent yield - server pre-computed, hidden when missing. One
            card only: it used to ALSO render RentYieldCard beneath,
            which repeated the same yield % + monthly rent (a visible
            duplicate); the only metric it added - payback years - is
            derivable from the summary (median price / annual rent), so
            it's folded into this card's strip instead. */}
        {summary.yield_pct != null && (
          <section className="mb-10">
            <SectionHeader
              title={t("district.section.yield")}
              icon={<TrendingUp className="w-4 h-4" />}
            />
            {(() => {
              // One number leads (the yield); the rent and the payback it
              // comes from are said in a sentence instead of a strip of
              // "~" tiles under a tinted icon square (owner, 2026-09-24:
              // the old card read as AI-generated).
              const rentM = summary.median_monthly_rent ?? null;
              const medPrice = summary.latest?.median_price ?? null;
              const payback = rentM && medPrice ? medPrice / (rentM * 12) : null;
              const sentence = rentM
                ? (payback != null && Number.isFinite(payback)
                    ? t("district.yield.sentence").replace("{years}", Math.round(payback).toString())
                    : t("district.yield.sentence_nopayback"))
                    // Whole numbers in a sentence ("8 400 AZN", not "8.4K").
                    .replace("{monthly}", `${formatPpm(rentM)} AZN`)
                    .replace("{annual}", `${formatPpm(Math.round((rentM * 12) / 100) * 100)} AZN`)
                : null;
              return (
                <div className="rounded-2xl border border-gray-200 bg-white p-5 sm:p-6">
                  <div className="text-[11px] font-semibold text-gray-400 mb-1">
                    {t("district.yield.label")}
                  </div>
                  <div className="text-4xl font-extrabold text-gray-900 tabular-nums leading-none">
                    {summary.yield_pct.toFixed(1).replace(".", locale === "en" ? "." : ",")}
                    <span className="text-2xl font-bold text-emerald-700 ml-0.5">%</span>
                  </div>
                  {sentence && (
                    <p className="mt-3 max-w-prose text-sm leading-relaxed text-gray-600">{sentence}</p>
                  )}
                </div>
              );
            })()}
          </section>
        )}

        {/* Interest (Pro): attention per listing here, last 4 weeks. */}
        <InterestCard kind="auto" slug={district.slug} className="mb-10" />

        {/* Top complexes */}
        {summary.top_complexes && summary.top_complexes.length > 0 && (
          <section className="mb-10">
            <SectionHeader
              title={t("district.complexes.title")}
              icon={<Building2 className="w-4 h-4" />}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {summary.top_complexes.map((c) => {
                // How this residence prices vs the district median - the
                // key comparison detail when browsing a district. Computed
                // client-side from the hero ppm already on the page, so no
                // extra backend round-trip.
                const premiumPct = ppm > 0 ? ((c.median_ppm - ppm) / ppm) * 100 : null;
                const showPremium = premiumPct != null && Math.abs(premiumPct) >= 1;
                const inner = (
                  <>
                    {/* Hero image (admin-hosted) or a grey placeholder */}
                    <div className="relative h-40 w-full overflow-hidden bg-gray-100">
                      {c.image_url ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <Photo src={mediaSrc(c.image_url)!} alt={c.name} className="transition-transform group-hover:scale-[1.02]" />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <Building2 className="h-12 w-12 text-gray-300" />
                        </div>
                      )}
                      {/* Premium vs district average */}
                      {showPremium && premiumPct != null && (
                        <span
                          title={fill(
                            premiumPct > 0 ? t("residence.premium.above") : t("residence.premium.below"),
                            { pct: Math.abs(premiumPct).toFixed(0) },
                          )}
                          className={`absolute top-2.5 right-2.5 rounded-full px-2 py-0.5 text-[11px] font-bold text-white backdrop-blur-sm ${
                            premiumPct > 0 ? "bg-[#d97706]/90" : "bg-[#059669]/90"
                          }`}
                        >
                          {signedNumber(premiumPct, 0)}%
                        </span>
                      )}
                      {/* Listing count as an overlay chip */}
                      <span className="absolute bottom-2.5 left-2.5 rounded-full bg-black/55 px-2.5 py-1 text-[11px] font-medium text-white backdrop-blur-sm">
                        {fill(t("district.complexes.listings"), { n: c.listing_count })}
                      </span>
                    </div>
                    <div className="p-4">
                      <div title={c.name} className="font-semibold text-gray-900 truncate">
                        {c.name}
                      </div>
                      <div className="mt-2 flex items-baseline gap-1.5">
                        <span className="text-2xl font-extrabold text-gray-900 tabular-nums leading-none">
                          {formatPpm(c.median_ppm)}
                        </span>
                        <span className="text-xs font-medium text-gray-500">AZN/m²</span>
                      </div>
                      {c.median_price ? (
                        <div className="mt-1 text-xs text-gray-500 tabular-nums">
                          ~{formatPpm(c.median_price)} AZN
                        </div>
                      ) : null}
                    </div>
                  </>
                );
                // Residences with a catalog cluster_id link through to
                // their own residence page; the rest stay static cards.
                return c.cluster_id ? (
                  <Link
                    key={c.name}
                    href={`/residence/${c.cluster_id}`}
                    className="group overflow-hidden rounded-2xl border border-gray-200 bg-white hover:border-gray-300 transition-colors"
                  >
                    {inner}
                  </Link>
                ) : (
                  <div
                    key={c.name}
                    className="overflow-hidden rounded-2xl border border-gray-200 bg-white"
                  >
                    {inner}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* FAQ */}
        {faq.length > 0 && (
          <section className="mb-10">
            <SectionHeader title={t("district.faq.title")} />
            <FaqList entries={faq} />
          </section>
        )}

      </main>
      {children}
    </div>
  );
}

// -- Small presentational helpers -------------------------------------------
